import React, { cloneElement, useEffect, useMemo, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { TextButton } from '../common/text-button';
import { IconButton } from '../common/icon-button';
import { TagIcon } from './assets';
import { MaterialTagData } from '@/services/material-tag-list';
import { Image } from '../common/image';
import { Video } from '../common/video';
import { TagItem } from './tag-item';
import { ModalTagCreate } from './modal-tag-create';

// 新增类型定义
type FileItem = File | { type: 'image' | 'video'; url: string; name?: string };

interface ModalTagChooseProps {
  trigger?: React.ReactElement;
  open?: boolean;
  title?: string;
  selectedIds?: number[];
  tags: MaterialTagData[];
  files: FileItem[];
  disabledIds?: number[];
  loading?: boolean;
  createLoading?: boolean;
  multiple?: boolean;
  onCreate?: (name: string) => Promise<void>;
  onConfirm?: (ids: number[]) => Promise<void>;
  onOpenChange?: (open: boolean) => void;
}

/** 标签选择弹窗 */
export const ModalTagChoose: React.FC<ModalTagChooseProps> = ({
  trigger,
  open: externalOpen,
  title,
  selectedIds: externalSelectedIds,
  tags,
  files,
  disabledIds,
  loading,
  multiple,
  createLoading,
  onCreate,
  onConfirm,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(externalSelectedIds || []);

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  useEffect(() => {
    setSelectedIds(externalSelectedIds || []);
  }, [externalSelectedIds]);

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };

  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 计算文件列表中有多少张图片，多少条视频
  const filesData = useMemo(() => {
    let imageCount = 0;
    let videoCount = 0;

    files.forEach(file => {
      if (file instanceof File) {
        // 处理 File 对象
        const fileName = file.name.toLowerCase();
        if (/\.(jpg|jpeg|png|webp|gif)$/i.test(fileName)) {
          imageCount++;
        } else if (/\.(mp4|mov|webm)$/i.test(fileName)) {
          videoCount++;
        }
      } else {
        // 处理 { type, url } 对象
        if (file.type === 'image') imageCount++;
        if (file.type === 'video') videoCount++;
      }
    });

    return { imageCount, videoCount };
  }, [files]);

  // 选择标签
  const handleSelectTag = (id: number) => {
    if (multiple) {
      setSelectedIds(prev => prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]);
    } else {
      setSelectedIds([id]);
    }
  }

  const handleConfirm = async () => {
    if (selectedIds.length === 0) return;
    await onConfirm?.(selectedIds);
    handleClose();
  }

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay
        className={cn("fixed inset-0 bg-black/50 z-50")}
      />
      <Dialog.Content
        modal-tag-move
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[440px] max-h-[540px] min-h-[540px] flex-shrink-0",
          "z-50 overflow-hidden select-none"
        )}
      >
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-5 pt-5 pb-4">
          <Dialog.Title
            className='focus:outline-none text-[rgba(16,24,40,1)] text-base font-semibold flex items-center'
          >
            <span>{title || t('assets.material_item.tag_choose_modal.title')}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              icon={<X className="size-6 text-[rgba(71,84,103,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        <div className='flex flex-1 flex-col px-5 overflow-hidden'>
          <div className='flex w-full items-center gap-1'>
            {/* 堆叠缩略图区域*/}
            <div className="relative h-9 w-[52px] flex-shrink-0">
              {files.slice(0, 3).map((file, index) => (
                <FilePreviewItem key={index} item={file} index={index} />
              ))}
            </div>
            <div className='text-base text-[rgba(16,24,40,1)] font-medium'>
              {filesData.imageCount > 0 && (
                <span>{filesData.imageCount} {t('assets.material_item.tag_choose_modal.photos')}</span>
              )}
              {(filesData.imageCount > 0 && filesData.videoCount > 0) && <span className='px-2'>·</span>}
              {filesData.videoCount > 0 && (
                <span>
                  {filesData.videoCount} {t('assets.material_item.tag_choose_modal.videos')}
                </span>
              )}
            </div>
          </div>
          <div className='mt-4 flex-1 flex flex-col min-h-0'>
            <h1 className='text-[rgba(16,24,40,1)] text-sm font-semibold mb-2'>
              {t('assets.material_item.tag_choose_modal.folder_tags')}
            </h1>

            {/* 滚动区域 */}
            <div className='flex-1 overflow-y-auto '>
              <div className='flex flex-wrap gap-[10px] pb-4'>
                <ModalTagCreate
                  trigger={
                    <TagItem className='flex items-center gap-1.5'>
                      <TagIcon className='size-4' />
                      <span>{t('assets.material_item.tag_choose_modal.create_tag')}</span>
                    </TagItem>
                  }
                  onConfirm={onCreate}
                  loading={createLoading}
                />
                {tags.map(tag => (
                  <TagItem
                    key={tag.id}
                    active={selectedIds.includes(tag.id)}
                    label={tag.tagName}
                    onClick={() => handleSelectTag(tag.id)}
                    disabled={disabledIds?.includes(tag.id)}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className='w-full h-[93px] flex justify-end items-center space-x-3 '>
            <TextButton
              label={t('assets.material_item.tag_choose_modal.cancel')}
              className='w-[120px] h-11 bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-semibold text-base !text-[rgba(65,70,81,1)]'
              onClick={handleClose}
            />
            <TextButton
              label={t('assets.material_item.tag_choose_modal.confirm')}
              className='w-[120px] h-11'
              disabled={loading || selectedIds.length === 0}
              loading={loading}
              onClick={handleConfirm}
            />
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

/** 文件预览项 - 支持 File 或 URL 对象 */
const FilePreviewItem = ({ item, index }: { item: FileItem; index: number }) => {
  const [displayUrl, setDisplayUrl] = useState<string>("");
  const [isImageType, setIsImageType] = useState(true);

  useEffect(() => {
    if (item instanceof File) {
      // 如果是 File 对象，生成预览 URL
      const objectUrl = URL.createObjectURL(item);
      setDisplayUrl(objectUrl);
      setIsImageType(item.type.startsWith("image/"));
      return () => URL.revokeObjectURL(objectUrl);
    } else {
      // 如果已经是对象格式，直接使用 url
      setDisplayUrl(item.url);
      setIsImageType(item.type === 'image');
    }
  }, [item]);

  if (!displayUrl) return null;

  // 样式计算保持不变
  const sizes = [36, 32, 28];
  const currentSize = sizes[index] || 28;
  const topOffset = (36 - currentSize) / 2;
  const leftOffset = index * 8;

  return (
    <div
      className="absolute rounded-[6px] overflow-hidden shadow-sm bg-gray-50 flex-shrink-0 transition-all"
      style={{
        width: `${currentSize}px`,
        height: `${currentSize}px`,
        top: `${topOffset}px`,
        left: `${leftOffset}px`,
        zIndex: 10 - index,
      }}
    >
      {isImageType ? (
        <Image src={displayUrl} className="size-full object-cover rounded-[6px]" />
      ) : (
        <Video src={displayUrl} className="size-full object-cover rounded-[6px]" preload="metadata" />
      )}

      {/* 蒙层逻辑保持不变 */}
      {index === 1 && <div className="absolute inset-0 bg-white/40 pointer-events-none" />}
      {index === 2 && <div className="absolute inset-0 bg-white/60 pointer-events-none" />}
    </div>
  );
};